package dev.lingvo.site.components.studio

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import dev.lingvo.shared.DeviceClass
import dev.lingvo.shared.ShotExportKind
import dev.lingvo.shared.ShotExportLayout
import dev.lingvo.shared.ShotExportManifest
import dev.lingvo.shared.StorePlatform
import dev.lingvo.site.api.API_JSON
import dev.lingvo.site.api.ApiClient
import dev.lingvo.site.api.Session
import dev.lingvo.site.api.screenshotExport
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.i18n.t
import dev.lingvo.site.pages.app.projects.slug.screenshots.exportDownloadFilename
import dev.lingvo.site.pages.app.projects.slug.screenshots.exportManifestText
import dev.lingvo.site.pages.app.projects.slug.screenshots.readCaptureBytes
import dev.lingvo.site.pages.app.projects.slug.screenshots.shotRequestFailure
import dev.lingvo.site.state.ProjectActionClaim
import dev.lingvo.site.state.ProjectPrivilege
import dev.lingvo.site.state.liveProjectRoute
import kotlinx.browser.document
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import kotlinx.serialization.decodeFromString
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob

/** The endpoint is credential-free; only the fetched Blob, never a bearer URL, becomes a link. */
@Composable
fun ExportDownload(slug: String, platform: StorePlatform, kind: ShotExportKind, devices: List<DeviceClass> = emptyList(), enabled: Boolean = true) {
    val scope = rememberCoroutineScope()
    val identity = Session.requestIdentity
    val claim = remember(slug, platform, identity) { ProjectActionClaim.capture(liveProjectRoute(slug), Session.organizations, ProjectPrivilege.READ) }
    var busy by remember(slug, platform, kind, identity) { mutableStateOf(false) }
    var error by remember(slug, platform, kind, identity) { mutableStateOf<String?>(null) }
    val authorized = { claim?.remainsAuthorized(liveProjectRoute(slug), Session.organizations) == true }
    LButton(t("studio.export_download.download_for_fastlane", "Download for fastlane"), {
        if (!busy && authorized()) scope.launch {
            busy = true; error = null
            try { downloadScreenshotExport(slug, platform, kind, devices, authorized) }
            catch (cancelled: CancellationException) { throw cancelled }
            catch (failure: Throwable) { if (authorized()) error = shotRequestFailure(failure) }
            finally { busy = false }
        }
    }, variant = LButtonVariant.SECONDARY, enabled = enabled && !busy, loading = busy)
    LMuted(if (!enabled) t("studio.export_download.no_images_to_download_yet", "No images to download yet.") else
        t("studio.export_download.zip_downloads_are_labelled_unreviewed_unless", "ZIP downloads are labelled unreviewed unless their manifest confirms an approved set."))
    error?.let { LAlert(it, variant = LAlertVariant.DANGER, live = true) }
}

/** Filename approval comes from this exact ZIP's manifest, never from a separate approval read. */
suspend fun downloadScreenshotExport(
    slug: String, platform: StorePlatform, kind: ShotExportKind, devices: List<DeviceClass>, authorized: () -> Boolean,
    save: suspend (Blob, String) -> Unit = { blob, name -> saveBlobDownload(blob, name) },
) {
    if (!authorized()) return
    val blob = ApiClient.screenshotExport(slug, platform, kind, ShotExportLayout.FASTLANE, devices = devices)
    if (!authorized()) return
    val manifest = readExportManifest(blob)
    if (!authorized()) return
    val approved = manifest?.let { it.platform == platform && it.kind == kind && it.layout == ShotExportLayout.FASTLANE && it.approvedSet } == true
    save(blob, exportDownloadFilename(platform, kind, approved))
}

/** Reads headers and the bounded manifest only: a large ZIP never becomes a second full-size byte array. */
private suspend fun readExportManifest(blob: Blob): ShotExportManifest? {
    var offset = 0.0
    repeat(2000) {
        if (offset + 30 > blob.size.toDouble()) return null
        val header = readCaptureBytes(blob.slice(offset.toInt(), (offset + 30).toInt()))
        fun u16(at: Int): Int = (header[at].toInt() and 255) or ((header[at + 1].toInt() and 255) shl 8)
        fun u32(at: Int): Long = (0..3).sumOf { (header[at + it].toLong() and 255) shl (8 * it) }
        if (header.size != 30 || u32(0) != 0x04034b50L || u16(6) and 9 != 0 || u16(8) != 0) return null
        val nameLength = u16(26)
        val headerEnd = offset + 30 + nameLength + u16(28)
        val size = u32(18)
        if (headerEnd + size > blob.size.toDouble() || size != u32(22)) return null
        val name = readCaptureBytes(blob.slice((offset + 30).toInt(), (offset + 30 + nameLength).toInt())).decodeToString()
        if (name == "manifest.json") {
            if (size > 2 * 1024 * 1024) return null
            val bytes = readCaptureBytes(blob.slice(offset.toInt(), (headerEnd + size).toInt()))
            val text = exportManifestText(bytes) ?: return null
            return runCatching { API_JSON.decodeFromString<ShotExportManifest>(text) }.getOrNull()
        }
        offset = headerEnd + size
    }
    return null
}

/** Always removes the temporary anchor and releases the URL, including cancellation and click failure. */
suspend fun saveBlobDownload(
    blob: Blob, filename: String, createUrl: (Blob) -> String = URL::createObjectURL,
    revokeUrl: (String) -> Unit = URL::revokeObjectURL, click: (HTMLAnchorElement) -> Unit = { it.click() },
) {
    val url = createUrl(blob)
    val anchor = document.createElement("a") as HTMLAnchorElement
    try {
        anchor.href = url; anchor.download = filename; anchor.style.display = "none"
        document.body?.appendChild(anchor)
        click(anchor)
        // Browsers need the URL to outlive the click dispatch before starting the download.
        delay(1000)
    } finally { anchor.remove(); revokeUrl(url) }
}
